import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  ArrowRightOnRectangleIcon,
  TrophyIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import { useAppPrefix } from '../../AppPrefixContext';
import { toast } from 'react-hot-toast';

export default function ScorerHome() {
  const p = useAppPrefix();
  const navigate = useNavigate();
  const { selectedTournament } = useTournament();
  const [userName, setUserName] = useState('');
  const [signingOut, setSigningOut] = useState(false);
  const [stats, setStats] = useState({ total: 0, complete: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
    if (selectedTournament?.id) loadStats(selectedTournament.id);
  }, [selectedTournament]);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase
        .from('auth_users')
        .select('full_name')
        .eq('id', user.id)
        .maybeSingle();
      setUserName(data?.full_name || user.email?.split('@')[0] || 'Scorer');
    }
  };

  const loadStats = async (tournamentId: string) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      // Only get rounds where this scorer has assignments
      const { data: assignments } = await supabase
        .from('scorer_assignments')
        .select('round_id')
        .eq('scorer_user_id', user.id);

      const assignedRoundIds = [...new Set((assignments ?? []).map((a: { round_id: string }) => a.round_id))];

      if (assignedRoundIds.length === 0) {
        setLoading(false);
        return;
      }

      const { data: rounds } = await supabase
        .from('tournament_rounds')
        .select('id, round_number')
        .eq('tournament_id', tournamentId)
        .in('id', assignedRoundIds)
        .order('round_number')
        .limit(1);

      if (!rounds || rounds.length === 0) {
        setLoading(false);
        return;
      }

      const roundId = rounds[0].id;
      const roundNumber = rounds[0].round_number;

      const { count: totalGroups } = await supabase
        .from('player_pairings')
        .select('group_number', { count: 'exact', head: true })
        .eq('round_id', roundId);

      const uniqueGroups = totalGroups ?? 0;

      const { data: holeScores } = await supabase
        .from('hole_scores')
        .select('player_id')
        .eq('tournament_id', tournamentId)
        .eq('round', roundNumber);

      setStats({ total: uniqueGroups, complete: holeScores ? new Set(holeScores.map(h => h.player_id)).size : 0 });
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      toast.success('Signed out');
      navigate(p);
    } catch {
      toast.error('Failed to sign out');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="pb-10">
      <div className="bg-golf-gray px-4 pt-4 pb-8">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Scorer Portal</p>
        <h1 className="text-2xl font-bold text-white">Welcome, {userName}</h1>
        {selectedTournament && (
          <p className="text-sm text-gray-400 mt-1">{selectedTournament.name}</p>
        )}
      </div>

      <div className="px-4 -mt-4 space-y-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-golf-orange/10 flex items-center justify-center">
              <TrophyIcon className="w-5 h-5 text-golf-orange" />
            </div>
            <div>
              <p className="font-bold text-golf-gray">Tournament Status</p>
              <p className="text-xs text-gray-400 capitalize">{selectedTournament?.status || '—'}</p>
            </div>
          </div>
          {!loading && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-golf-gray">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-0.5">Total Groups</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{stats.complete}</p>
                <p className="text-xs text-gray-400 mt-0.5">Players Scored</p>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => navigate(`${p}/scorer/groups`)}
          className="w-full bg-golf-orange rounded-2xl p-5 flex items-center gap-4 active:scale-[0.98] transition-transform text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <ClipboardDocumentListIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-white text-base">Enter Scores</p>
            <p className="text-sm text-white/80 mt-0.5">Browse pairing groups and enter hole scores</p>
          </div>
        </button>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">How It Works</p>
          <div className="space-y-3">
            {[
              { n: '1', text: 'Select the active round and browse pairing groups' },
              { n: '2', text: 'Tap a group to open the scorecard entry view' },
              { n: '3', text: 'Enter strokes for each player on each hole (1–18)' },
              { n: '4', text: 'Save progress at any time or submit the full scorecard' },
            ].map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-golf-orange flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-white">{step.n}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleSignOut}
          disabled={signingOut}
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl border border-red-100 bg-red-50 active:bg-red-100 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5 text-red-500" />
          <span className="text-sm font-semibold text-red-600">
            {signingOut ? 'Signing out...' : 'Sign Out'}
          </span>
        </button>
      </div>
    </div>
  );
}
